import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { User } from '../types';
import apiService from '../services/api';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (
    display_name: string,
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    dateOfBirth: string
  ) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAuthStatus = async () => {
      const token = localStorage.getItem('authToken');
      if (token) {
        try {
          const userData = await apiService.getUserProfile();
          setUser(userData);
        } catch (error: any) {
          const msg = error?.message?.toLowerCase() || '';
          const isAuthFailure =
            msg.includes('expired') ||
            msg.includes('invalid token') ||
            msg.includes('session') ||
            msg.includes('403') ||
            msg.includes('unauthorized') ||
            msg.includes('jwt');
          if (isAuthFailure) {
            localStorage.removeItem('authToken');
            localStorage.removeItem('refreshToken');
            setUser(null);
          }
          // Don't clear token on 429, 500, or network errors – user stays logged in
        }
      } else {
        setUser(null);
      }
      setIsLoading(false);
    };
    checkAuthStatus();
  }, []);

  const login = useCallback(async (emailOrUsername: string, password: string) => {
    try {
      const response = await apiService.login(emailOrUsername, password);
      if (response.success && response.user) {
        localStorage.setItem('authToken', response.token);
        if (response.refreshToken) {
          localStorage.setItem('refreshToken', response.refreshToken);
        }
        setUser(response.user);
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      throw error;
    }
  }, []);

  const register = useCallback(
    async (
      display_name: string,
      email: string,
      password: string,
      firstName: string,
      lastName: string,
      dateOfBirth: string
    ) => {
      const response = await apiService.register(
        display_name,
        email,
        password,
        firstName,
        lastName,
        dateOfBirth
      );
      if (response.success && response.user) {
        if (response.token) {
          localStorage.setItem('authToken', response.token);
        }
        if (response.refreshToken) {
          localStorage.setItem('refreshToken', response.refreshToken);
        }
        setUser(response.user);
      } else {
        throw new Error(response.message || 'Registration failed');
      }
    },
    []
  );

  const logout = useCallback(() => {
    localStorage.removeItem('authToken');
    setUser(null);
    // Refresh the page and redirect to home to clear all cached data
    window.location.href = '/';
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated: !!user,
      isLoading,
      login,
      register,
      logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 